import React from "react";

import { IndividualApplicationEntryPointCard } from "IndividualApplication/Components/IndividualApplicationEntryPointCard";
import { IndividualEligibilityHelper } from "IndividualApplication/utils";
import { IndividualLifeCard } from "IndividualLife/IndividualLifeCard";
import { IndividualLifeEligible } from "IndividualLife/utils";
import isIntuit from "Intuit/isIntuit";
import { redirectName } from "QuotePage/urls";
import isSquare from "Square/isSquare";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { isDeclinedCompletedPolicy } from "Utils/EmployeeUtils";
import BasicLoading from "ZeroRuby/BasicLoading";

import { Title } from "Cobalt/Header";
import BodyText from "Common/BodyText";
import { CardHeader, CardSection } from "Common/Card";
import { RoutedBackButton } from "Common/forms/buttons";
import { Row, Col } from "Common/Grid";
import Icon from "Common/Icon";
import { appendAuthParams } from "Common/TokenAuth/Utils";
import Button from "Theme/Button";

import { useGetApplicationQuery } from "./api";
import Page from "./Page";
import { signURL } from "./urls";

const DefaultMessage = () => (
  <BodyText componentClass="p" size="large" agent>
    Our team will be in touch once your application is approved.
  </BodyText>
);

const DecliningAllCoverageMessage = ({ employeeForm, adminMode }) => (
  <BodyText componentClass="p" size="large" agent>
    You have declined all coverage.
    {!adminMode && <EmployeeDashboardLink employeeForm={employeeForm} />}
  </BodyText>
);

const EmployeeDashboardLink = ({ employeeForm }) => (
  <>
    <a href={`/small-group/employee-dashboard?employee_id_token=${employeeForm.getShortIDToken}`}> Click here </a>
    to access your employee dashboard.
  </>
);

const employerDashboardLink = (companyIDToken) => {
  const params = companyIDToken ? `?company_id_token=${companyIDToken}` : "";
  return appendAuthParams(`${redirectName}${params}`);
};

const DonePageWrapper = () => {
  const { data: employeeApplication, error: _error, isLoading } = useGetApplicationQuery();

  const props = {
    employeePolicy: employeeApplication?.employeePolicy,
    availablePlans: employeeApplication?.availablePlans,
    employeeForm: employeeApplication?.employeePolicy.employeeForm,
    adminMode: employeeApplication?.adminMode,
    linkedAccount: employeeApplication?.linkedAccount,
    companyIDToken: employeeApplication?.companyIDToken,
  };

  props.hideIndividualApplications = props.employeeForm?.hideIndividualApplications;
  const individualEligiblePlans = IndividualEligibilityHelper(props.availablePlans, props.employeeForm);
  props.individualEligiblePlans = individualEligiblePlans;
  props.individualApplicationsAvailable = props.employeeForm?.individualApplicationsAvailable;
  props.individualApplicationStatus = props.employeeForm?.individualApplicationStatus;

  const individualLifeEligible = IndividualLifeEligible(props.employeeForm || {});
  props.individualLifeEligible = individualLifeEligible;

  if (individualEligiblePlans.includes("dental")) {
    AnalyticsTracking.trackEvent("individual_dental_entrypoint_viewed", {
      location: "application",
    });
  }
  if (individualEligiblePlans.includes("vision")) {
    AnalyticsTracking.trackEvent("individual_vision_entrypoint_viewed", {
      location: "application",
    });
  }

  return isLoading ? <BasicLoading /> : <DonePage {...props} />;
};

const DoneButton = ({ companyIDToken }) => (
  <Button size="medium" href={employerDashboardLink(companyIDToken)}>
    Return to Manage Employees
  </Button>
);

const renderCards = ({
  individualEligiblePlans,
  individualLifeEligible,
  individualApplicationStatus,
  individualApplicationsAvailable,
  adminMode,
  employeeForm,
}) => (
  <Row mt={4}>
    {individualEligiblePlans.length > 0 && (
      <Col md={6}>
        <IndividualApplicationEntryPointCard
          individualApplicationStatus={individualApplicationStatus}
          individualApplicationsAvailable={individualApplicationsAvailable}
          individualEligiblePlans={individualEligiblePlans}
        />
      </Col>
    )}

    {individualLifeEligible && (
      <Col md={6}>
        <IndividualLifeCard adminMode={adminMode} location="employee-application-done" employeeForm={employeeForm} />
      </Col>
    )}
  </Row>
);

const DonePage = ({
  employeePolicy,
  employeeForm,
  adminMode,
  linkedAccount,
  hideIndividualApplications,
  individualEligiblePlans,
  individualLifeEligible,
  individualApplicationsAvailable,
  individualApplicationStatus,
  companyIDToken,
}) => (
  <Page title="You are done">
    <CardHeader>
      You are done! <Icon icon="smile" />
    </CardHeader>
    <CardSection>
      {isDeclinedCompletedPolicy(employeePolicy) ? <DecliningAllCoverageMessage employeeForm={employeeForm} /> : <DefaultMessage />}

      {!(isSquare() || isIntuit()) && (
        <BodyText componentClass="p" size="large" agent>
          If you have questions in the meantime, please check out our{" "}
          <a href="https://support.simplyinsured.com/s/topic/0TO3t000000auQuGAI/simplyinsured-for-employees">FAQ for Employees</a>.
        </BodyText>
      )}
      {!hideIndividualApplications && linkedAccount && (individualEligiblePlans.length > 0 || individualLifeEligible) && (
        <>
          <Title mt={5} mb={4} pt={5}>
            You have completed enrollment for the health benefits offered from your company. If you would like to enroll in additional benefits for yourself,
            you can do so here.
          </Title>
          <BodyText mb={6}>
            These benefits are not covered by your employer so you will be responsible for payment of these benefits directly to the insurance carrier.
          </BodyText>
          {renderCards({
            individualEligiblePlans,
            individualLifeEligible,
            individualApplicationsAvailable,
            individualApplicationStatus,
            adminMode,
            employeeForm,
          })}
        </>
      )}
    </CardSection>
    {(adminMode || !employeePolicy.effectiveAt) && (
      <CardSection className="clearfix">
        <Row>
          <Col sm={3}>{!isDeclinedCompletedPolicy(employeePolicy) && <RoutedBackButton href={signURL(adminMode, false, employeeForm.hashID)} />}</Col>
          <Col sm={6} smOffset={3} className="text-right">
            {adminMode && <DoneButton companyIDToken={companyIDToken} />}
          </Col>
        </Row>
      </CardSection>
    )}
  </Page>
);

export default DonePageWrapper;
